import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';

const PrivacyPage = () => {
  const navigate = useNavigate();
  const lastUpdated = "February 1, 2026";

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50 sticky top-0 bg-background/95 backdrop-blur z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-lg font-semibold">Privacy Policy</h1>
        </div>
      </header>

      <ScrollArea className="h-[calc(100vh-65px)]">
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="prose prose-invert prose-sm max-w-none">
            <p className="text-muted-foreground text-sm mb-8">Last Updated: {lastUpdated}</p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">1. Introduction</h2>
              <p className="text-muted-foreground mb-4">
                Arlo (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) respects your privacy and is committed to protecting your personal 
                data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you use our AI personal assistant service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg font-medium mb-3 mt-6">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>Account Information:</strong> Email address, password (encrypted), and profile details you provide during registration</li>
                <li><strong>Profile Data:</strong> Name, job title, company, industry, and preferences you share during onboarding</li>
                <li><strong>Conversations:</strong> Messages and content you input when using the Service</li>
                <li><strong>Memories:</strong> Facts and preferences you save for personalized assistance</li>
                <li><strong>Uploaded Documents:</strong> Files you upload to the Knowledge Base feature</li>
                <li><strong>Payment Information:</strong> Billing details processed securely through Stripe</li>
              </ul>

              <h3 className="text-lg font-medium mb-3 mt-6">2.2 Information Collected Automatically</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>Usage Data:</strong> Features used, session duration, and interaction patterns</li>
                <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers</li>
                <li><strong>Log Data:</strong> IP address, access times, and pages viewed</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">3. How We Use Your Information</h2>
              <p className="text-muted-foreground mb-4">We use your information to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Provide and personalize the AI assistant service</li>
                <li>Process your requests and generate AI responses</li>
                <li>Maintain your memories and knowledge base for context</li>
                <li>Process payments and manage subscriptions</li>
                <li>Send service-related communications</li>
                <li>Improve and develop the Service</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">4. AI Processing and Data Usage</h2>
              <p className="text-muted-foreground mb-4">
                <strong>How AI Uses Your Data:</strong> When you send a message, we process it along with relevant 
                context (your memories, profile, and knowledge base) to generate personalized responses. This 
                processing happens in real-time.
              </p>
              <p className="text-muted-foreground mb-4">
                <strong>Temporary Chat:</strong> Messages sent in temporary chat mode are processed but not stored 
                in our database. They are deleted after your session ends.
              </p>
              <p className="text-muted-foreground mb-4">
                <strong>Third-Party AI Providers:</strong> We use Claude (by Anthropic) as our AI provider. Your 
                conversation data is sent to their API for processing. Please review 
                <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline"> Anthropic&apos;s Privacy Policy</a> for 
                details on their data handling practices.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">5. Data Sharing and Disclosure</h2>
              <p className="text-muted-foreground mb-4">We may share your information with:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>Service Providers:</strong> Third parties that help us operate the Service (AI providers, payment processors, email services)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              </ul>
              <p className="text-muted-foreground mb-4">
                <strong>We do NOT:</strong> Sell your personal information to third parties or use your conversations 
                to train our AI models without your explicit consent.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">6. Data Retention</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>Account Data:</strong> Retained while your account is active</li>
                <li><strong>Conversations:</strong> Stored until you delete them or your account</li>
                <li><strong>Memories:</strong> Retained until you delete them</li>
                <li><strong>Temporary Chats:</strong> Not stored; deleted after session</li>
                <li><strong>Payment Records:</strong> Retained as required by tax and accounting laws</li>
              </ul>
              <p className="text-muted-foreground mb-4">
                Upon account deletion, we will delete your personal data within 30 days, except where retention 
                is required by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">7. Data Security</h2>
              <p className="text-muted-foreground mb-4">We implement industry-standard security measures including:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Encryption of data in transit (TLS/SSL) and at rest</li>
                <li>Secure password hashing (bcrypt)</li>
                <li>Access controls and authentication</li>
                <li>Regular security assessments</li>
              </ul>
              <p className="text-muted-foreground mb-4">
                However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">8. Your Rights and Choices</h2>
              <p className="text-muted-foreground mb-4">Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate data</li>
                <li><strong>Deletion:</strong> Request deletion of your data</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Objection:</strong> Object to certain processing activities</li>
                <li><strong>Withdrawal:</strong> Withdraw consent where processing is based on consent</li>
              </ul>
              <p className="text-muted-foreground mb-4">
                To exercise these rights, contact us at <a href="mailto:privacy@heyarlo.ai" className="text-emerald-400 hover:underline">privacy@heyarlo.ai</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">9. Cookies and Tracking</h2>
              <p className="text-muted-foreground mb-4">
                We use essential cookies to maintain your session and authentication. We do not use tracking 
                cookies or third-party advertising cookies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">10. International Data Transfers</h2>
              <p className="text-muted-foreground mb-4">
                Your data may be transferred to and processed in countries other than your own. We ensure appropriate 
                safeguards are in place for such transfers in compliance with applicable data protection laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">11. Children&apos;s Privacy</h2>
              <p className="text-muted-foreground mb-4">
                The Service is not intended for users under 16 years of age. We do not knowingly collect personal 
                information from children. If you believe we have collected data from a child, please contact us 
                immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">12. Changes to This Policy</h2>
              <p className="text-muted-foreground mb-4">
                We may update this Privacy Policy periodically. We will notify you of material changes by posting 
                the new policy on the Service with an updated &quot;Last Updated&quot; date. We encourage you to review 
                this policy regularly.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">13. Contact Us</h2>
              <p className="text-muted-foreground mb-4">
                For questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p className="text-muted-foreground mb-4">
                Email: <a href="mailto:privacy@heyarlo.ai" className="text-emerald-400 hover:underline">privacy@heyarlo.ai</a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">14. California Privacy Rights (CCPA)</h2>
              <p className="text-muted-foreground mb-4">
                California residents have additional rights under the California Consumer Privacy Act (CCPA), 
                including the right to know what personal information is collected, request deletion, and opt-out 
                of the sale of personal information. We do not sell personal information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">15. European Privacy Rights (GDPR)</h2>
              <p className="text-muted-foreground mb-4">
                If you are in the European Economic Area (EEA), you have rights under the General Data Protection 
                Regulation (GDPR). Our legal basis for processing your data is: (a) your consent, (b) performance 
                of a contract, (c) our legitimate interests, or (d) compliance with legal obligations.
              </p>
            </section>
          </div>
        </main>
      </ScrollArea>
    </div>
  );
};

export default PrivacyPage;
